import { useEffect, useMemo, useRef, useState } from 'react'
import { MaterialIcon } from '@/ui/material-icon'
import { Button } from '@/ui/button'
import { Input } from '@/ui/input'
import { Label } from '@/ui/label'
import { Checkbox } from '@/ui/checkbox'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { authApi } from '@/features/auth/api/auth.api'
import {
    GetUserModuleConfigResponse,
    SaveModuleConfigItem,
    TenantModule,
    TenantModuleFeature,
    UserModuleConfigItem,
} from '@/features/auth/types'
import { STORAGE_KEYS } from '@/shared/constants'
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/ui/select'
import * as Dialog from '@radix-ui/react-dialog'
import styles from './NavigationBuilder.module.scss'
import { Separator } from '@/ui/separator'
import { AVAILABLE_ICONS, ICON_CATEGORIES } from '@/shared/constants/iconList'

type NavMenuItem = {
    id: string
    label: string
    icon: string
    path: string
    visible: boolean
    hasSubmenu: boolean
    enabledFor: string[]
    children: NavMenuItem[]
    moduleId?: number
    moduleName?: string
    featureId?: number
    featureName?: string
    group?: string
    isPlatform?: boolean
    isDefault?: boolean
    isGroup?: boolean
    order?: number
    position?: 'main' | 'footer'
}

type DragInfo = {
    id: string
    parentId: string | null
    index: number
    fromSection?: string
}

type ItemMeta = {
    id: string
    parentId: string | null
    depth: number
}

const MAX_GROUP_DEPTH = 2 // 0-based depth: 0,1,2 => 3 levels total
const FOOTER_DROP_TARGET = '__footer__'

const DEFAULT_ICON = 'LayoutDashboard'
const ROLE_OPTIONS = ['admin', 'user'] as const

const ICON_NAME_MAP: Record<string, string> = {
    LayoutDashboard: 'dashboard',
    Wrench: 'build',
    Palette: 'palette',
    Headphones: 'headset_mic',
    Settings: 'settings',
}

const resolveMaterialIconName = (iconName?: string): string => {
    if (!iconName) return 'dashboard'
    if (ICON_NAME_MAP[iconName]) return ICON_NAME_MAP[iconName]
    return iconName
        .replace(/([a-z0-9])([A-Z])/g, '$1_$2')
        .replace(/\s+/g, '_')
        .toLowerCase()
}

// Helper to get icon for each category
const getCategoryIcon = (category: string): string => {
    const categoryIcons: Record<string, string> = {
        'Navigation': 'navigation',
        'Actions': 'build',
        'UI': 'palette',
        'Media': 'music_note',
        'Communication': 'chat',
        'Users': 'people',
        'Emotions': 'mood',
        'Files': 'folder',
        'Analytics': 'show_chart',
        'Development': 'code',
        'Status': 'info',
        'Arrows': 'arrow_forward',
        'Common': 'star',
        'DateTime': 'calendar_today',
        'Location': 'location_on',
        'Devices': 'devices',
        'Business': 'business',
        'Sports': 'sports_soccer',
        'Transportation': 'directions_car',
        'Food': 'restaurant',
        'Shopping': 'shopping_cart',
        'Services': 'local_hospital',
        'Money': 'attach_money',
        'Cloud': 'cloud',
        'Lists': 'list',
        'MediaControls': 'play_arrow',
        'Books': 'menu_book',
        'Labels': 'label',
        'Medical': 'medical_services',
        'Family': 'family_restroom',
        'Celebrations': 'celebration',
        'Other': 'apps'
    }
    return categoryIcons[category] || 'apps'
}

const getItemTypeBadges = (item: NavMenuItem): string[] => {
    const badges: string[] = []
    const isGroup = item.isGroup === true || item.id.startsWith('group_')
    const isFeatureItem = item.featureId != null

    if (isGroup) badges.push('Group')
    if (item.featureId != null) badges.push('Feature')
    if (item.moduleId != null && !isGroup && !isFeatureItem) {
        if (item.isDefault === true) badges.push('Syst Module')
        else badges.push('Module')
    }

    return badges
}

const getTypeBadgeClassName = (badge: string): string => {
    const styleMap = styles as Record<string, string>
    if (badge === 'Feature') return styleMap.typeBadgeFeature ?? ''
    if (badge === 'Module') return styleMap.typeBadgeModule ?? ''
    if (badge === 'Syst Module') return styleMap.typeBadgeSystem ?? ''
    return ''
}

const getItemTypeLabel = (item?: NavMenuItem | null): string => {
    if (!item) return 'Module'
    if (item.isGroup === true || item.id.startsWith('group_')) return 'Group'
    if (item.featureId != null) return 'Feature'
    if (item.moduleId != null) return item.isDefault === true ? 'Syst Module' : 'Module'
    return 'Module'
}

const isFooterItem = (item: NavMenuItem): boolean => {
    const idLower = String(item.id ?? '').toLowerCase()
    const labelLower = String(item.label ?? '').toLowerCase()
    const path = item.path || ''

    if (path === '/support' || path === '/settings') return true
    if (idLower === 'support' || idLower === 'settings') return true
    if (labelLower === 'support' || labelLower === 'settings') return true
    if (item.isPlatform === false) return true

    return false
}

const sortItemsByOrder = (items: NavMenuItem[]): NavMenuItem[] => {
    return [...items]
        .map((item) => ({
            ...item,
            children: sortItemsByOrder(item.children),
        }))
        .sort((a, b) => {
            const aOrder = a.order ?? Number.MAX_SAFE_INTEGER
            const bOrder = b.order ?? Number.MAX_SAFE_INTEGER
            return aOrder - bOrder
        })
}

const toNavItem = (item: UserModuleConfigItem, inheritedModuleId?: number): NavMenuItem => {
    const typedItem = item as UserModuleConfigItem & { feature_id?: number; feature_name?: string; is_group?: boolean }
    const moduleId = item.module_id ?? inheritedModuleId
    const childrenSource = (item as any).features ?? (item as any).children ?? []
    const children = (childrenSource as UserModuleConfigItem[])
        .map((child) => toNavItem(child, moduleId))
    const id = item.id ?? item.path ?? item.label
    return {
        id,
        label: item.label,
        icon: item.icon || DEFAULT_ICON,
        path: item.path || '',
        visible: true,
        hasSubmenu: children.length > 0,
        enabledFor: ['admin'],
        children,
        moduleId,
        featureId: typedItem.feature_id,
        featureName: typedItem.feature_name,
        group: item.group,
        position: item.position ?? (item.is_platform === false ? 'footer' : undefined),
        isPlatform: item.is_platform ?? item.position !== 'footer',
        isDefault: item.is_default,
        isGroup: typedItem.is_group,
        order: item.order,
    }
}

const findItemById = (items: NavMenuItem[], id: string): NavMenuItem | null => {
    for (const item of items) {
        if (item.id === id) return item
        if (item.children.length > 0) {
            const found = findItemById(item.children, id)
            if (found) return found
        }
    }
    return null
}

const updateItemInTree = (
    items: NavMenuItem[],
    id: string,
    updater: (item: NavMenuItem) => NavMenuItem
): NavMenuItem[] => {
    return items.map((item) => {
        if (item.id === id) {
            return updater(item)
        }
        if (item.children.length === 0) return item
        return {
            ...item,
            children: updateItemInTree(item.children, id, updater),
        }
    })
}

const deleteItemFromTree = (items: NavMenuItem[], id: string): NavMenuItem[] => {
    return items
        .filter((item) => item.id !== id)
        .map((item) => ({
            ...item,
            children: deleteItemFromTree(item.children, id),
        }))
}

const updateGroupInTree = (
    items: NavMenuItem[],
    fromGroup: string,
    toGroup: string
): NavMenuItem[] => {
    return items.map((item) => {
        const currentGroup = item.group || 'Menu'
        return {
            ...item,
            group: currentGroup === fromGroup ? toGroup : item.group,
            children: updateGroupInTree(item.children, fromGroup, toGroup),
        }
    })
}

const reorderAtSameLevel = (
    items: NavMenuItem[],
    parentId: string | null,
    fromIndex: number,
    toIndex: number
): NavMenuItem[] => {
    if (fromIndex === toIndex) return items

    const move = (list: NavMenuItem[]) => {
        if (
            fromIndex < 0 ||
            fromIndex >= list.length ||
            toIndex < 0 ||
            toIndex >= list.length
        ) {
            return list
        }
        const copy = [...list]
        const [moved] = copy.splice(fromIndex, 1)
        if (!moved) return list
        copy.splice(toIndex, 0, moved)
        return copy
    }

    if (!parentId) {
        return move(items)
    }

    return items.map((item) => {
        if (item.id === parentId) {
            return {
                ...item,
                children: move(item.children),
            }
        }
        if (item.children.length === 0) return item
        return {
            ...item,
            children: reorderAtSameLevel(item.children, parentId, fromIndex, toIndex),
        }
    })
}

const flattenVisibleItems = (items: NavMenuItem[]): NavMenuItem[] => {
    return items.filter((item) => item.visible)
}

const collectItemMeta = (
    items: NavMenuItem[],
    parentId: string | null = null,
    depth = 0,
    acc: ItemMeta[] = []
): ItemMeta[] => {
    items.forEach((item) => {
        acc.push({ id: item.id, parentId, depth })
        if (item.children.length > 0) {
            collectItemMeta(item.children, item.id, depth + 1, acc)
        }
    })
    return acc
}

const getRelativeSubtreeDepth = (item: NavMenuItem): number => {
    if (!item.children.length) return 0
    return 1 + Math.max(...item.children.map(getRelativeSubtreeDepth))
}

const collectOriginalModuleIds = (
    items: UserModuleConfigItem[],
    inheritedModuleId?: number,
    acc: Map<string, number> = new Map()
): Map<string, number> => {
    items.forEach((item) => {
        const id = item.id ?? item.path ?? item.label
        const moduleId = item.module_id ?? inheritedModuleId
        if (moduleId != null) {
            acc.set(id, moduleId)
        }
        const childrenSource = (item as any).features ?? (item as any).children ?? []
        if (childrenSource.length > 0) {
            collectOriginalModuleIds(childrenSource, moduleId, acc)
        }
    })
    return acc
}

// Helper: extract a single item (with all children) from the tree
const extractItemFromTree = (
    items: NavMenuItem[],
    id: string
): { extracted: NavMenuItem | null; remaining: NavMenuItem[] } => {
    let extracted: NavMenuItem | null = null
    const remaining = items
        .map((item) => {
            if (item.id === id) {
                extracted = item
                return null
            }
            if (item.children.length > 0) {
                const result = extractItemFromTree(item.children, id)
                if (result.extracted) {
                    extracted = result.extracted
                    return { ...item, children: result.remaining }
                }
            }
            return item
        })
        .filter(Boolean) as NavMenuItem[]
    return { extracted, remaining }
}

// Helper: flatten all items (with children) into a single list for searching
const flattenAllItems = (items: NavMenuItem[], parentLabel?: string): Array<NavMenuItem & { parentLabel?: string }> => {
    let result: Array<NavMenuItem & { parentLabel?: string }> = []
    items.forEach((item) => {
        result.push({ ...item, parentLabel })
        if (item.children.length > 0) {
            result = result.concat(flattenAllItems(item.children, item.label))
        }
    })
    return result
}

const collectBranchIds = (items: NavMenuItem[]): string[] => {
    const ids: string[] = []
    const walk = (nodes: NavMenuItem[]) => {
        nodes.forEach((node) => {
            if (node.hasSubmenu && node.children.length > 0) {
                ids.push(node.id)
                walk(node.children)
            }
        })
    }
    walk(items)
    return ids
}

export default function NavigationBuilder() {
    const storedUser = localStorage.getItem(STORAGE_KEYS.USER)
    const userId = storedUser ? (JSON.parse(storedUser) as { id?: string }).id : undefined
    const queryClient = useQueryClient()
    const [menuItems, setMenuItems] = useState<NavMenuItem[]>([])
    const [selectedId, setSelectedId] = useState<string | null>(null)
    const initialMenuItemsRef = useRef<NavMenuItem[] | null>(null)
    const [dragging, setDragging] = useState<DragInfo | null>(null)
    const [dragOverId, setDragOverId] = useState<string | null>(null)
    const [dragOverSection, setDragOverSection] = useState<string | null>(null)
    const [checkedIds, setCheckedIds] = useState<string[]>([])
    const [treeCollapsed, setTreeCollapsed] = useState(false)
    const [propertiesCollapsed, setPropertiesCollapsed] = useState(false)
    const [collapsedBranchIds, setCollapsedBranchIds] = useState<Set<string>>(() => new Set())
    const [collapsedSections, setCollapsedSections] = useState<Set<string>>(() => new Set())
    const [previewCollapsedBranchIds, setPreviewCollapsedBranchIds] = useState<Set<string>>(
        () => new Set()
    )
    const [editingSectionName, setEditingSectionName] = useState<string | null>(null)
    const [editingSectionDraft, setEditingSectionDraft] = useState('')
    const [editingGroupId, setEditingGroupId] = useState<string | null>(null)
    const [editingGroupDraft, setEditingGroupDraft] = useState('')
    const [emptySections, setEmptySections] = useState<Set<string>>(() => new Set())

    // Search module state
    const [moduleSearchTerm, setModuleSearchTerm] = useState('')

    // Edit item modal state
    const [editingItemId, setEditingItemId] = useState<string | null>(null)
    const [editingItemDraft, setEditingItemDraft] = useState<Partial<NavMenuItem>>({})
    const [iconSelectionOpen, setIconSelectionOpen] = useState(false)
    const [iconSearchTerm, setIconSearchTerm] = useState('')

    const {
        data: moduleConfigResponse,
        isLoading: isModuleConfigLoading,
        isError: moduleConfigError,
    } = useQuery<GetUserModuleConfigResponse, Error>({
        queryKey: ['appearance', 'navigation-builder', userId],
        queryFn: () => authApi.getUserModuleConfig(userId as string),
        enabled: Boolean(userId),
        staleTime: 5 * 60 * 1000,
        retry: 0,
    })

    const {
        data: tenantModules = [],
        isLoading: isModulesLoading,
        isError: modulesError,
    } = useQuery<TenantModule[], Error>({
        queryKey: ['rbac', 'modules'],
        queryFn: () => authApi.getTenantModules(),
        staleTime: 5 * 60 * 1000,
    })

    const [selectedModuleId, setSelectedModuleId] = useState<number | null>(null)

    const {
        data: moduleFeatures = [],
        isLoading: isFeaturesLoading,
        isError: featuresError,
    } = useQuery<TenantModuleFeature[], Error>({
        queryKey: ['rbac', 'module-features', selectedModuleId],
        queryFn: () => authApi.getTenantModuleFeatures(selectedModuleId as number),
        enabled: selectedModuleId != null,
        staleTime: 5 * 60 * 1000,
    })

    const selectedItem = selectedId ? findItemById(menuItems, selectedId) : null
    const originalModuleIdByItemId = useMemo(
        () => collectOriginalModuleIds(moduleConfigResponse?.data ?? []),
        [moduleConfigResponse]
    )
    const editingGroupItem = editingGroupId ? findItemById(menuItems, editingGroupId) : null
    const editingItem = editingItemId ? findItemById(menuItems, editingItemId) : null
    const sidebarItems = useMemo(
        () => menuItems.filter((item) => !isFooterItem(item)),
        [menuItems]
    )
    const footerItems = useMemo(
        () => menuItems.filter((item) => isFooterItem(item)),
        [menuItems]
    )
    const sidebarSections = useMemo(() => {
        const grouped = new Map<string, NavMenuItem[]>()
        sidebarItems.forEach((item) => {
            const sectionName = item.group || 'Menu'
            if (!grouped.has(sectionName)) grouped.set(sectionName, [])
            grouped.get(sectionName)?.push(item)
        })
        // Also include empty sections that have no items yet
        emptySections.forEach((sectionName) => {
            if (!grouped.has(sectionName)) grouped.set(sectionName, [])
        })
        return Array.from(grouped.entries()).map(([name, items]) => ({ name, items }))
    }, [sidebarItems, emptySections])

    // Filtered sections based on search term
    const filteredSidebarSections = useMemo(() => {
        if (!moduleSearchTerm.trim()) return sidebarSections

        const term = moduleSearchTerm.toLowerCase().trim()

        return sidebarSections
            .map((section) => {
                const filterItems = (items: NavMenuItem[]): NavMenuItem[] => {
                    return items.reduce<NavMenuItem[]>((acc, item) => {
                        const labelMatch = item.label.toLowerCase().includes(term)
                        const filteredChildren = filterItems(item.children)
                        if (labelMatch || filteredChildren.length > 0) {
                            acc.push({ ...item, children: filteredChildren })
                        }
                        return acc
                    }, [])
                }

                const filteredItems = filterItems(section.items)
                const sectionNameMatch = section.name.toLowerCase().includes(term)

                if (sectionNameMatch || filteredItems.length > 0) {
                    return { ...section, items: sectionNameMatch ? section.items : filteredItems }
                }
                return null
            })
            .filter(Boolean) as typeof sidebarSections
    }, [sidebarSections, moduleSearchTerm])

    const sidebarMetaById = useMemo(() => {
        const map = new Map<string, ItemMeta>()
        collectItemMeta(sidebarItems).forEach((meta) => {
            map.set(meta.id, meta)
        })
        return map
    }, [sidebarItems])
    const selectedCheckedMeta = useMemo(
        () => checkedIds.map((id) => sidebarMetaById.get(id)).filter(Boolean) as ItemMeta[],
        [checkedIds, sidebarMetaById]
    )
    const selectedGroupsForUngroup = useMemo(
        () => {
            const selectedSet = new Set(checkedIds)
            const groups: NavMenuItem[] = []
            const walk = (nodes: NavMenuItem[]) => {
                nodes.forEach((node) => {
                    if (selectedSet.has(node.id) && node.hasSubmenu && node.children.length > 0) {
                        groups.push(node)
                    }
                    if (node.children.length > 0) {
                        walk(node.children)
                    }
                })
            }
            walk(sidebarItems)
            return groups
        },
        [checkedIds, sidebarItems]
    )

    useEffect(() => {
        const fetchedItems = moduleConfigResponse?.data ?? []
        if (fetchedItems.length === 0) return
        const normalized = sortItemsByOrder(fetchedItems.map(toNavItem))
        initialMenuItemsRef.current = normalized
        setMenuItems(normalized)
        setSelectedId((prev) => prev ?? normalized[0]?.id ?? null)
    }, [moduleConfigResponse])

    useEffect(() => {
        if (!selectedItem?.moduleId) {
            setSelectedModuleId(null)
            return
        }
        setSelectedModuleId(selectedItem.moduleId)
    }, [selectedItem?.moduleId])

    useEffect(() => {
        if (!selectedItem || selectedItem.moduleId != null || !selectedId) return

        const meta = sidebarMetaById.get(selectedId)
        if (!meta?.parentId) return

        const parent = findItemById(menuItems, meta.parentId)
        if (!parent?.moduleId) return

        setMenuItems((prev) =>
            updateItemInTree(prev, selectedId, (item) => ({
                ...item,
                moduleId: parent.moduleId,
                moduleName: parent.moduleName,
            }))
        )
    }, [menuItems, selectedId, selectedItem, sidebarMetaById])

    const routeOptions = useMemo(
        () =>
            [
                '/dashboard',
                '/reports',
                '/roles',
                '/appearance',
                '/users',
                '/support',
                '/settings',
                '/module-feature-configuration',
                '/coming-soon',
            ] as const,
        []
    )

    const handleLabelChange = (val: string) => {
        if (!selectedId) return
        setMenuItems((prev) => updateItemInTree(prev, selectedId, (item) => ({ ...item, label: val })))
    }

    const handlePathChange = (val: string) => {
        if (!selectedId) return
        setMenuItems((prev) =>
            updateItemInTree(prev, selectedId, (item) => ({
                ...item,
                path: val === '__none__' ? '' : val,
            }))
        )
    }

    const handleIconChange = (val: string) => {
        if (!selectedId) return
        setMenuItems((prev) => updateItemInTree(prev, selectedId, (item) => ({ ...item, icon: val })))
    }

    const handleVisibleChange = (val: boolean) => {
        if (!selectedId) return
        setMenuItems((prev) => updateItemInTree(prev, selectedId, (item) => ({ ...item, visible: val })))
    }

    const handleSubmenuChange = (val: boolean) => {
        if (!selectedId) return
        setMenuItems((prev) =>
            updateItemInTree(prev, selectedId, (item) => ({
                ...item,
                hasSubmenu: val,
                children: val ? item.children : [],
                path: val ? '' : item.path,
            }))
        )
    }

    const handleAddFeature = () => {
        if (!selectedId) return

        setMenuItems((prev) =>
            updateItemInTree(prev, selectedId, (item) => {
                const numberedFeatureIndexes = item.children
                    .map((child) => {
                        const match = child.label.match(/^Feature\s+(\d+)$/)
                        return match ? Number(match[1]) : 0
                    })
                    .filter((num) => Number.isFinite(num))
                const nextFeatureIndex =
                    numberedFeatureIndexes.length > 0
                        ? Math.max(...numberedFeatureIndexes) + 1
                        : item.children.length + 1

                const featureItem: NavMenuItem = {
                    id: `feature_${Date.now()}_${item.children.length + 1}`,
                    label: `Feature ${nextFeatureIndex}`,
                    icon: DEFAULT_ICON,
                    path: '',
                    visible: true,
                    hasSubmenu: false,
                    enabledFor: [...item.enabledFor],
                    children: [],
                    moduleId: item.moduleId,
                    moduleName: item.moduleName,
                    group: item.group,
                    isPlatform: item.isPlatform,
                }

                return {
                    ...item,
                    hasSubmenu: true,
                    path: '',
                    children: [...item.children, featureItem],
                }
            })
        )
    }

    const handleRoleToggle = (role: string, checked: boolean) => {
        if (!selectedId) return
        setMenuItems((prev) =>
            updateItemInTree(prev, selectedId, (item) => ({
                ...item,
                enabledFor: checked
                    ? Array.from(new Set([...item.enabledFor, role]))
                    : item.enabledFor.filter((entry) => entry !== role),
            }))
        )
    }

    const handleModuleChange = (value: string) => {
        if (!selectedId) return
        if (value === '__none__') {
            setMenuItems((prev) =>
                updateItemInTree(prev, selectedId, (item) => ({
                    ...item,
                    moduleId: undefined,
                    moduleName: undefined,
                    featureId: undefined,
                    featureName: undefined,
                }))
            )
            setSelectedModuleId(null)
            return
        }

        const moduleId = Number(value)
        const module = tenantModules.find((m) => m.module_id === moduleId)
        setMenuItems((prev) =>
            updateItemInTree(prev, selectedId, (item) => ({
                ...item,
                moduleId,
                moduleName: module?.module_name,
                featureId: undefined,
                featureName: undefined,
            }))
        )
        setSelectedModuleId(moduleId)
    }

    const handleFeatureChange = (value: string) => {
        if (!selectedId) return
        if (value === '__none__') {
            setMenuItems((prev) =>
                updateItemInTree(prev, selectedId, (item) => ({
                    ...item,
                    featureId: undefined,
                    featureName: undefined,
                }))
            )
            return
        }

        const featureId = Number(value)
        const feature = moduleFeatures.find((f) => f.feature_id === featureId)
        setMenuItems((prev) =>
            updateItemInTree(prev, selectedId, (item) => ({
                ...item,
                featureId,
                featureName: feature?.feature_name,
            }))
        )
    }

    const handleDelete = () => {
        if (!selectedId) return
        const remaining = deleteItemFromTree(menuItems, selectedId)
        setMenuItems(remaining)
        setSelectedId(remaining[0]?.id ?? null)
        setCheckedIds((prev) => prev.filter((id) => id !== selectedId))
    }

    const handleAddItem = () => {
        const newId = `menu_${Date.now()}`
        const next: NavMenuItem = {
            id: newId,
            label: `Menu item ${menuItems.length + 1}`,
            icon: DEFAULT_ICON,
            path: '',
            visible: true,
            hasSubmenu: false,
            enabledFor: ['admin'],
            children: [],
            group: 'Admin',
            isPlatform: true,
        }
        setMenuItems((prev) => [...prev, next])
        setSelectedId(newId)
    }

    const handleAddSection = () => {
        const existing = new Set(sidebarSections.map((section) => section.name))
        let sectionNumber = sidebarSections.length + 1
        let sectionName = `Section ${sectionNumber}`
        while (existing.has(sectionName)) {
            sectionNumber += 1
            sectionName = `Section ${sectionNumber}`
        }

        setEmptySections((prev) => new Set([...prev, sectionName]))
        toast.success(`Section "${sectionName}" created.`)
    }

    const handleDeleteSection = (sectionName: string) => {
        setEmptySections((prev) => {
            const next = new Set(prev)
            next.delete(sectionName)
            return next
        })
    }

    const startSectionEdit = (sectionName: string) => {
        setEditingSectionName(sectionName)
        setEditingSectionDraft(sectionName)
    }

    const cancelSectionEdit = () => {
        setEditingSectionName(null)
        setEditingSectionDraft('')
    }

    const saveSectionEdit = () => {
        if (!editingSectionName) return
        const nextName = editingSectionDraft.trim()

        if (!nextName) {
            toast.error('Section name cannot be empty.')
            return
        }
        if (nextName === editingSectionName) {
            cancelSectionEdit()
            return
        }
        if (sidebarSections.some((section) => section.name === nextName)) {
            toast.error('A section with this name already exists.')
            return
        }

        setEmptySections((prev) => {
            if (!prev.has(editingSectionName!)) return prev
            const next = new Set(prev)
            next.delete(editingSectionName!)
            next.add(nextName)
            return next
        })

        setMenuItems((prev) => updateGroupInTree(prev, editingSectionName, nextName))
        cancelSectionEdit()
    }

    const startGroupEdit = (groupId: string) => {
        const groupNode = findItemById(menuItems, groupId)
        if (!groupNode) return
        setEditingGroupId(groupId)
        setEditingGroupDraft(groupNode.label)
    }

    const cancelGroupEdit = () => {
        setEditingGroupId(null)
        setEditingGroupDraft('')
    }

    const ungroupByIds = (selectedSet: Set<string>) => {
        const ungroupTree = (nodes: NavMenuItem[]): NavMenuItem[] => {
            const out: NavMenuItem[] = []
            nodes.forEach((node) => {
                if (selectedSet.has(node.id) && node.hasSubmenu && node.children.length > 0) {
                    out.push(...node.children)
                    return
                }
                out.push({
                    ...node,
                    children: ungroupTree(node.children),
                })
            })
            return out
        }

        const nextItems = ungroupTree(menuItems)
        setMenuItems(nextItems)
        setCheckedIds((prev) => prev.filter((id) => !selectedSet.has(id)))
        if (selectedId && selectedSet.has(selectedId)) {
            setSelectedId(nextItems[0]?.id ?? null)
        }
    }

    const saveGroupEdit = () => {
        if (!editingGroupId) return
        const nextName = editingGroupDraft.trim()
        if (!nextName) {
            toast.error('Group name cannot be empty.')
            return
        }
        setMenuItems((prev) => updateItemInTree(prev, editingGroupId, (item) => ({ ...item, label: nextName })))
        cancelGroupEdit()
    }

    const deleteGroupFromModal = () => {
        if (!editingGroupId) return
        ungroupByIds(new Set([editingGroupId]))
        cancelGroupEdit()
    }

    // Edit item modal handlers
    const openItemEdit = (itemId: string) => {
        const node = findItemById(menuItems, itemId)
        if (!node) return
        setEditingItemId(itemId)
        setEditingItemDraft({
            label: node.label,
            icon: node.icon,
            path: node.path,
            visible: node.visible,
        })
        setSelectedId(itemId)
    }

    const cancelItemEdit = () => {
        setEditingItemId(null)
        setEditingItemDraft({})
    }

    const saveItemEdit = () => {
        if (!editingItemId) return
        const label = (editingItemDraft.label ?? '').trim()
        if (!label) {
            toast.error('Label cannot be empty.')
            return
        }
        setMenuItems((prev) =>
            updateItemInTree(prev, editingItemId, (item) => ({
                ...item,
                label,
                icon: editingItemDraft.icon ?? item.icon,
                path: editingItemDraft.path ?? item.path,
                visible: editingItemDraft.visible ?? item.visible,
            }))
        )
        cancelItemEdit()
        toast.success('Menu item updated.')
    }

    const deleteItemFromModal = () => {
        if (!editingItemId) return
        const remaining = deleteItemFromTree(menuItems, editingItemId)
        setMenuItems(remaining)
        setSelectedId(remaining[0]?.id ?? null)
        setCheckedIds((prev) => prev.filter((id) => id !== editingItemId))
        cancelItemEdit()
        toast.success('Menu item deleted.')
    }

    const toggleCheckedId = (id: string, checked: boolean) => {
        setCheckedIds((prev) =>
            checked ? Array.from(new Set([...prev, id])) : prev.filter((entry) => entry !== id)
        )
    }

    const handleGroupSelected = () => {
        if (selectedCheckedMeta.length < 2) {
            toast.error('Select at least two menu items to group.')
            return
        }
        const firstMeta = selectedCheckedMeta[0]
        if (!firstMeta) return
        const sameLevelAndParent = selectedCheckedMeta.every(
            (meta) => meta.depth === firstMeta.depth && meta.parentId === firstMeta.parentId
        )
        if (!sameLevelAndParent) {
            toast.error('Only same-level siblings can be grouped together.')
            return
        }
        const resultingMaxDepth = selectedCheckedMeta.reduce((maxDepth, meta) => {
            const node = findItemById(menuItems, meta.id)
            if (!node) return maxDepth
            const nodeMaxDepthAfterGrouping = meta.depth + 1 + getRelativeSubtreeDepth(node)
            return Math.max(maxDepth, nodeMaxDepthAfterGrouping)
        }, 0)
        if (resultingMaxDepth > MAX_GROUP_DEPTH) {
            toast.error('Grouping this selection exceeds 3 levels.')
            return
        }
        const selectedSet = new Set(selectedCheckedMeta.map((meta) => meta.id))
        const groupId = `group_${Date.now()}`
        const groupItem: NavMenuItem = {
            id: groupId,
            label: 'Group Selected',
            icon: 'folder',
            path: '',
            visible: true,
            hasSubmenu: true,
            enabledFor: ['admin'],
            children: [],
            group: 'Admin',
            isPlatform: true,
        }
        const replaceAtParent = (nodes: NavMenuItem[]): NavMenuItem[] => {
            if (firstMeta.parentId == null) {
                const targetSiblings = nodes.filter((item) => !isFooterItem(item))
                const firstIndex = targetSiblings.findIndex((item) => selectedSet.has(item.id))
                if (firstIndex < 0) return nodes
                const groupedChildren = targetSiblings.filter((item) => selectedSet.has(item.id))
                const merged = nodes.filter((item) => !selectedSet.has(item.id))
                groupItem.children = groupedChildren
                merged.splice(firstIndex, 0, groupItem)
                return merged
            }

            return nodes.map((node) => {
                if (node.id === firstMeta.parentId) {
                    const firstIndex = node.children.findIndex((child) => selectedSet.has(child.id))
                    if (firstIndex < 0) return node
                    const groupedChildren = node.children.filter((child) => selectedSet.has(child.id))
                    const remainingChildren = node.children.filter((child) => !selectedSet.has(child.id))
                    groupItem.children = groupedChildren
                    remainingChildren.splice(firstIndex, 0, groupItem)
                    return {
                        ...node,
                        children: remainingChildren,
                    }
                }
                if (node.children.length === 0) return node
                return {
                    ...node,
                    children: replaceAtParent(node.children),
                }
            })
        }

        const nextItems = replaceAtParent(menuItems)
        setMenuItems(nextItems)
        setCheckedIds([groupId])
        setSelectedId(groupId)
    }

    const handleUngroupSelected = () => {
        const selectedSet = new Set(selectedGroupsForUngroup.map((item) => item.id))
        if (selectedSet.size === 0) {
            toast.error('Select at least one grouped item to ungroup.')
            return
        }
        ungroupByIds(selectedSet)
    }

    const getIndexInParent = (items: NavMenuItem[], parentId: string | null, itemId: string): number => {
        if (!parentId) {
            return items.findIndex((item) => item.id === itemId)
        }
        const parent = findItemById(items, parentId)
        return parent ? parent.children.findIndex((item) => item.id === itemId) : -1
    }

    const startDrag = (id: string, parentId: string | null, index: number, fromSection?: string) => {
        const draggedItem = findItemById(menuItems, id)
        if (!draggedItem) return

        setDragging({ id, parentId, index, fromSection })
    }

    const dropOn = (targetId: string, parentId: string | null) => {
        if (!dragging) return
        if (dragging.id === targetId) return
        if (dragging.parentId !== parentId) return

        const fromIndex = getIndexInParent(menuItems, dragging.parentId, dragging.id)
        const toIndex = getIndexInParent(menuItems, parentId, targetId)
        if (fromIndex < 0 || toIndex < 0) {
            setDragging(null)
            setDragOverId(null)
            return
        }

        setMenuItems((prev) =>
            reorderAtSameLevel(prev, parentId, fromIndex, toIndex)
        )
        setDragging(null)
        setDragOverId(null)
    }

    // Drop onto section drop zone — appends items (allows multiple drops)
    const dropOntoEmptySection = (sectionName: string) => {
        if (!dragging) return
        if (dragging.parentId != null) return

        const draggedItem = findItemById(menuItems, dragging.id)
        if (!draggedItem) return

        const { remaining } = extractItemFromTree(menuItems, dragging.id)

        const reassignSection = (item: NavMenuItem): NavMenuItem => ({
            ...item,
            group: sectionName === FOOTER_DROP_TARGET ? item.group : sectionName,
            position: sectionName === FOOTER_DROP_TARGET ? 'footer' : 'main',
            isPlatform: sectionName === FOOTER_DROP_TARGET ? false : true,
            children: item.children.map(reassignSection),
        })

        const movedItem = reassignSection(draggedItem)

        if (sectionName === FOOTER_DROP_TARGET) {
            setMenuItems([...remaining, movedItem])
        } else {
            const targetSectionItems = remaining.filter(
                (item) => !isFooterItem(item) && (item.group || 'Menu') === sectionName
            )
            const allOtherItems = remaining.filter(
                (item) => isFooterItem(item) || (item.group || 'Menu') !== sectionName
            )

            targetSectionItems.push(movedItem)
            setMenuItems([...allOtherItems, ...targetSectionItems])
        }

        setEmptySections((prev) => {
            const next = new Set(prev)
            next.delete(sectionName)
            return next
        })

        setDragging(null)
        setDragOverSection(null)
        setDragOverId(null)
    }

    const toggleBranchCollapse = (id: string) => {
        setCollapsedBranchIds((prev) => {
            const next = new Set(prev)
            if (next.has(id)) next.delete(id)
            else next.add(id)
            return next
        })
    }

    const toggleSectionCollapse = (sectionName: string) => {
        setCollapsedSections((prev) => {
            const next = new Set(prev)
            if (next.has(sectionName)) next.delete(sectionName)
            else next.add(sectionName)
            return next
        })
    }

    const handleExpandSection = (sectionItems: NavMenuItem[]) => {
        const ids = collectBranchIds(sectionItems)
        setCollapsedBranchIds((prev) => {
            const next = new Set(prev)
            ids.forEach((id) => next.delete(id))
            return next
        })
    }

    const handleCollapseSection = (sectionItems: NavMenuItem[]) => {
        const ids = collectBranchIds(sectionItems)
        setCollapsedBranchIds((prev) => {
            const next = new Set(prev)
            ids.forEach((id) => next.add(id))
            return next
        })
    }

    const togglePreviewBranchCollapse = (id: string) => {
        setPreviewCollapsedBranchIds((prev) => {
            const next = new Set(prev)
            if (next.has(id)) next.delete(id)
            else next.add(id)
            return next
        })
    }

    const renderMenuItems = (
        items: NavMenuItem[],
        parentId: string | null = null,
        depth = 0,
        isFooter = false,
        sectionName?: string
    ): React.ReactNode => {
        const depthClass =
            depth <= 0
                ? ''
                : depth === 1
                    ? styles.depthOne
                    : depth === 2
                        ? styles.depthTwo
                        : styles.depthThree
        return items.map((item, index) => {
            const hasNestedChildren = item.hasSubmenu && item.children.length > 0
            const branchCollapsed = hasNestedChildren && collapsedBranchIds.has(item.id)
            const isThirdLevel = depth === 2
            const typeBadges = getItemTypeBadges(item)

            const isModuleRoot = item.moduleId != null && item.featureId == null
            const isDraggable = true

            return (
                <div key={item.id}>
                    <div className={styles.menuItemRowWrapper}>
                        <div
                            className={`${depth > 0 ? styles.subMenuListItem : styles.menuListItem} ${depthClass} ${isFooter ? styles.menuListItemFooter : ''} ${selectedId === item.id ? styles.menuListItemActive : ''} ${dragOverId === item.id ? styles.menuListItemDragOver : ''}`}
                            onClick={() => setSelectedId(item.id)}
                            draggable={isDraggable}
                            onDragStart={(e) => {
                                e.dataTransfer.effectAllowed = 'move'
                                e.dataTransfer.setData('text/plain', item.id)
                                startDrag(item.id, parentId, index, sectionName)
                            }}
                            onDragOver={(e: React.DragEvent<HTMLDivElement>) => {
                                e.preventDefault()
                                setDragOverId(item.id)
                            }}
                            onDragLeave={() => setDragOverId((prev) => (prev === item.id ? null : prev))}
                            onDrop={() => dropOn(item.id, parentId)}
                        >
                            <span className={styles.dragHandle}>⠿</span>
                            {!isModuleRoot && (
                                <Checkbox
                                    checked={checkedIds.includes(item.id)}
                                    onClick={(e) => e.stopPropagation()}
                                    onCheckedChange={(checked) => toggleCheckedId(item.id, checked === true)}
                                />
                            )}
                            {depth === 0 && (
                                <MaterialIcon
                                    name={resolveMaterialIconName(item.icon)}
                                    size={18}
                                    className={styles.menuItemIcon}
                                />
                            )}
                            {isThirdLevel && <span className={styles.thirdLevelDot} aria-hidden />}
                            {/* Label takes flex:1 to push edit+chevron to the right */}
                            <span className={styles.menuItemLabel}>{item.label}</span>
                            {!item.visible && <span className={styles.hiddenBadge}>Hidden</span>}

                            {/* Edit button — always visible, always same position */}
                            <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                className={styles.menuItemEditBtn}
                                aria-label={`Edit ${item.label}`}
                                onClick={(e) => {
                                    e.stopPropagation()
                                    openItemEdit(item.id)
                                }}
                                onMouseDown={(e) => e.stopPropagation()}
                            >
                                <MaterialIcon name="edit" size={14} />
                            </Button>

                            {/*
                             * Chevron slot — ALWAYS rendered as either:
                             *   a) the real expand/collapse button (if hasNestedChildren)
                             *   b) a static rightArrow icon (if hasSubmenu but no children yet)
                             *   c) an invisible placeholder span (if no submenu at all)
                             * This ensures the edit button always sits in the same column.
                             */}
                            {hasNestedChildren ? (
                                <Button
                                    type="button"
                                    variant="ghost"
                                    size="icon"
                                    className={styles.treeExpandBtn}
                                    aria-expanded={!branchCollapsed}
                                    aria-label={branchCollapsed ? 'Expand submenu' : 'Collapse submenu'}
                                    onClick={(e) => {
                                        e.stopPropagation()
                                        toggleBranchCollapse(item.id)
                                    }}
                                    onMouseDown={(e) => e.stopPropagation()}
                                >
                                    <MaterialIcon
                                        name={branchCollapsed ? 'chevron_right' : 'expand_more'}
                                        size={18}
                                    />
                                </Button>
                            ) : item.hasSubmenu ? (
                                /* hasSubmenu=true but no children yet — show static arrow in same slot */
                                <span className={styles.treeExpandBtn} aria-hidden>
                                    <MaterialIcon
                                        name="chevron_right"
                                        size={18}
                                        className={styles.rightArrow}
                                    />
                                </span>
                            ) : (
                                /* No submenu at all — invisible placeholder preserves column width */
                                <span className={styles.chevronPlaceholder} aria-hidden />
                            )}
                        </div>

                        {/* Badges outside the bordered div, with gap from menuItemRowWrapper */}
                        {typeBadges.length > 0 && (
                            <span className={styles.menuItemTrailOuter}>
                                {typeBadges.map((badge) => (
                                    <span
                                        key={`${item.id}_${badge}`}
                                        className={`${styles.typeBadge} ${getTypeBadgeClassName(badge)}`.trim()}
                                    >
                                        {badge}
                                        {badge === 'Group' && item.id.startsWith('group_') && (
                                            <Button
                                                type="button"
                                                variant="ghost"
                                                size="icon"
                                                className={styles.groupBadgeEditBtn}
                                                onClick={(e) => {
                                                    e.stopPropagation()
                                                    startGroupEdit(item.id)
                                                }}
                                                onMouseDown={(e) => e.stopPropagation()}
                                                aria-label={`Edit group ${item.label}`}
                                            >
                                                <MaterialIcon name="edit" size={12} />
                                            </Button>
                                        )}
                                    </span>
                                ))}
                            </span>
                        )}
                    </div>
                    {hasNestedChildren && !branchCollapsed && (
                        <div className={styles.subMenuNested}>
                            {renderMenuItems(item.children, item.id, depth + 1, isFooter, sectionName)}
                        </div>
                    )}
                </div>
            )
        })
    }

    const renderPreviewItems = (
        items: NavMenuItem[],
        depth = 0
    ): React.ReactNode => {
        const isThirdLevel = depth === 2
        const previewDepthClass =
            depth <= 0
                ? ''
                : depth === 1
                    ? styles.previewDepthOne
                    : depth === 2
                        ? styles.previewDepthTwo
                        : styles.previewDepthThree
        return flattenVisibleItems(items).map((item) => {
            const hasNestedChildren = item.hasSubmenu && item.children.length > 0
            const previewBranchCollapsed = hasNestedChildren && previewCollapsedBranchIds.has(item.id)

            return (
                <div key={item.id}>
                    <div className={`${depth > 0 ? styles.previewSubItem : styles.previewItem} ${previewDepthClass}`}>
                        {depth === 0 && (
                            <MaterialIcon
                                name={resolveMaterialIconName(item.icon)}
                                size={18}
                                className={styles.menuItemIcon}
                            />
                        )}
                        {isThirdLevel && <span className={styles.thirdLevelDot} aria-hidden />}
                        <span className={styles.menuItemLabel}>{item.label}</span>
                        {item.hasSubmenu && (
                            hasNestedChildren ? (
                                <Button
                                    type="button"
                                    variant="ghost"
                                    size="icon"
                                    className={styles.previewExpandBtn}
                                    aria-expanded={!previewBranchCollapsed}
                                    aria-label={previewBranchCollapsed ? 'Expand submenu' : 'Collapse submenu'}
                                    onClick={() => togglePreviewBranchCollapse(item.id)}
                                >
                                    <MaterialIcon
                                        name={previewBranchCollapsed ? 'chevron_right' : 'expand_more'}
                                        size={14}
                                    />
                                </Button>
                            ) : (
                                <MaterialIcon name="chevron_right" size={14} className={styles.rightArrow} />
                            )
                        )}
                    </div>
                    {hasNestedChildren && !previewBranchCollapsed && (
                        depth === 0 ? (
                            <div className={styles.previewSubTree}>
                                {renderPreviewItems(item.children, depth + 1)}
                            </div>
                        ) : (
                            renderPreviewItems(item.children, depth + 1)
                        )
                    )}
                </div>
            )
        })
    }

    const buildSavePayload = (items: NavMenuItem[]): SaveModuleConfigItem[] => {
        const getResolvedModuleId = (item: NavMenuItem): number | undefined => {
            return item.moduleId ?? originalModuleIdByItemId.get(item.id)
        }

        const serializeChildren = (
            nodes: NavMenuItem[],
            moduleId: number
        ): SaveModuleConfigItem['children'] => {
            return nodes
                .map((node, index) => {
                    const nodeModuleId = getResolvedModuleId(node)
                    if (nodeModuleId != null && nodeModuleId !== moduleId) return null

                    const children = serializeChildren(node.children, moduleId)
                    return {
                        id: node.id,
                        label: node.label,
                        feature_id: node.featureId,
                        path: node.path || undefined,
                        is_group: node.id.startsWith('group_') ? true : undefined,
                        order: index + 1,
                        ...(children.length > 0 ? { children } : {}),
                    }
                })
                .filter(Boolean) as SaveModuleConfigItem['children']
        }

        const modulePayloadById = new Map<number, SaveModuleConfigItem>()

        const walk = (nodes: NavMenuItem[], parentModuleId?: number) => {
            nodes.forEach((item) => {
                const resolvedModuleId = getResolvedModuleId(item)
                const startsNewModuleRoot =
                    resolvedModuleId != null &&
                    (parentModuleId == null || parentModuleId !== resolvedModuleId)

                if (startsNewModuleRoot) {
                    modulePayloadById.set(resolvedModuleId, {
                        label: item.label,
                        icon: item.icon || DEFAULT_ICON,
                        group: item.group || 'Admin',
                        is_platform: item.isPlatform ?? true,
                        position: item.position ?? (item.isPlatform === false ? 'footer' : undefined),
                        module_id: resolvedModuleId,
                        path: item.path || undefined,
                        children: serializeChildren(item.children, resolvedModuleId),
                    })
                }

                walk(item.children, resolvedModuleId ?? parentModuleId)
            })
        }

        walk(items)
        return Array.from(modulePayloadById.values()).map((item, index) => ({
            ...item,
            order: index + 1,
        }))
    }

    const saveMutation = useMutation({
        mutationFn: (payload: SaveModuleConfigItem[]) => authApi.saveModuleConfig(payload),
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: ['appearance', 'navigation-builder'],
            })
            queryClient.invalidateQueries({
                queryKey: ['rbac', 'module-config'],
            })
            toast.success('Navigation saved successfully.')
        },
        onError: () => {
            toast.error('Failed to save navigation config.')
        },
    })

    const handleResetNavigation = () => {
        const initialMenu = initialMenuItemsRef.current
        if (!initialMenu) return

        setMenuItems(initialMenu)
        setSelectedId(initialMenu[0]?.id ?? null)
        setCheckedIds([])
        setCollapsedBranchIds(new Set())
        setCollapsedSections(new Set())
        setPreviewCollapsedBranchIds(new Set())
        setDragOverId(null)
        setDragOverSection(null)
        setIconSelectionOpen(false)
        cancelItemEdit()
    }

    const handleSaveNavigation = () => {
        const payload = buildSavePayload(menuItems)
        saveMutation.mutate(payload)
    }

    return (
        <div className={styles.navBuilder}>
            <div
                className={`${styles.navColumns} ${treeCollapsed ? styles.navColumnsTreeCollapsed : ''}`}
            >
                <div className={styles.navColumn}>
                    <div className={styles.columnHeaderRow}>
                        <div className={styles.columnHeaderRowTitle}>
                            <h3 className={styles.columnTitle}>Preview</h3>
                        </div>
                    </div>
                    <div className={styles.previewCanvas}>
                        <div className={styles.previewShell}>
                            <div className={styles.previewList}>
                                {sidebarSections.map((section) => (
                                    <div key={`preview_${section.name}`} className={styles.previewSectionBlock}>
                                        <div className={styles.previewSectionTitle}>{section.name}</div>
                                        {renderPreviewItems(section.items)}
                                    </div>
                                ))}
                            </div>
                            <div className={styles.previewFooter}>
                                {renderPreviewItems(footerItems)}
                                <div className={styles.previewVersion}>Version 1.02.09</div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className={`${styles.navColumn} ${treeCollapsed ? styles.navColumnCollapsed : ''}`}>
                    <div className={styles.columnHeaderRow}>
                        {!treeCollapsed && (
                            <div className={styles.columnHeaderRowTitle}>
                                <h3 className={styles.columnTitle}>Navigation Tree</h3>
                                <p className={styles.columnSubtitle}>Drag and drop to reorder (same level)</p>
                            </div>
                        )}
                        <div className={styles.headerActions}>
                            <Button variant="outline" className={styles.resetBtn} onClick={handleResetNavigation}>
                                Reset
                            </Button>
                            <Button onClick={handleSaveNavigation} disabled={saveMutation.isPending}>
                                {saveMutation.isPending ? 'Saving...' : 'Save Navigation'}
                            </Button>
                        </div>
                    </div>
                    {!treeCollapsed && (
                        <>
                            <Separator />

                            {/* Search Module bar */}
                            <div className={styles.moduleSearchBar}>
                                <div className={styles.moduleSearchContainer}>
                                    <MaterialIcon name="search" size={16} className={styles.moduleSearchIcon} />
                                    <input
                                        type="text"
                                        placeholder="Search module"
                                        value={moduleSearchTerm}
                                        onChange={(e) => setModuleSearchTerm(e.target.value)}
                                        className={styles.moduleSearchInput}
                                    />
                                    {moduleSearchTerm && (
                                        <button
                                            className={styles.moduleSearchClearBtn}
                                            onClick={() => setModuleSearchTerm('')}
                                            type="button"
                                        >
                                            <MaterialIcon name="close" size={14} />
                                        </button>
                                    )}
                                </div>
                            </div>

                            <div className={styles.menuListSection}>
                                <div className={styles.menuList}>
                                    {isModuleConfigLoading && (
                                        <div className={styles.loadingInline}>Loading menu config...</div>
                                    )}
                                    {moduleConfigError && (
                                        <div className={styles.errorInline}>Failed to load menu config.</div>
                                    )}
                                    {!isModuleConfigLoading && filteredSidebarSections.length === 0 && (
                                        <div className={styles.loadingInline}>
                                            {moduleSearchTerm ? `No modules found for "${moduleSearchTerm}"` : 'No sections yet. Add one to start.'}
                                        </div>
                                    )}
                                    {!isModuleConfigLoading && filteredSidebarSections.map((section) => {
                                        const isEmpty = section.items.length === 0 && emptySections.has(section.name)
                                        const isDropTarget = dragOverSection === section.name
                                        const showDropZone = true

                                        return (
                                            <div key={section.name} className={styles.menuSectionBlock}>
                                                <div className={styles.treeSectionHeader}>
                                                    <div className={styles.completeHeader}>
                                                        <div className={styles.menuHeader}>
                                                            <span className={styles.treeSectionHeaderTitle}>{section.name}</span>
                                                            <Button
                                                                type="button"
                                                                variant="ghost"
                                                                size="icon"
                                                                className={styles.sectionEditBtn}
                                                                onClick={() => startSectionEdit(section.name)}
                                                                aria-label={`Edit ${section.name} section`}
                                                            >
                                                                <MaterialIcon name="edit" size={14} />
                                                            </Button>
                                                            <Button
                                                                type="button"
                                                                className={styles.sectionAccordionBtn}
                                                                onClick={() => toggleSectionCollapse(section.name)}
                                                                aria-expanded={!collapsedSections.has(section.name)}
                                                            >
                                                                <MaterialIcon
                                                                    name={collapsedSections.has(section.name) ? 'chevron_right' : 'expand_more'}
                                                                    size={16}
                                                                />
                                                            </Button>
                                                            {isEmpty && (
                                                                <Button
                                                                    type="button"
                                                                    variant="ghost"
                                                                    size="icon"
                                                                    className={styles.sectionDeleteBtn}
                                                                    onClick={() => handleDeleteSection(section.name)}
                                                                    aria-label={`Delete ${section.name} section`}
                                                                >
                                                                    <MaterialIcon name="delete" size={14} />
                                                                </Button>
                                                            )}
                                                        </div>
                                                        <div>
                                                            {collectBranchIds(section.items).length > 0 && (() => {
                                                                const allExpanded = collectBranchIds(section.items).every(id => !collapsedBranchIds.has(id))
                                                                return (
                                                                    <button
                                                                        type="button"
                                                                        className={styles.sectionExpandBtn}
                                                                        onClick={() => allExpanded
                                                                            ? handleCollapseSection(section.items)
                                                                            : handleExpandSection(section.items)
                                                                        }
                                                                    >
                                                                        <MaterialIcon name={allExpanded ? 'unfold_less' : 'unfold_more'} size={13} />
                                                                        {allExpanded ? 'Collapse all' : 'Expand all'}
                                                                    </button>
                                                                )
                                                            })()}
                                                        </div>
                                                    </div>
                                                </div>
                                                <Separator />

                                                {!collapsedSections.has(section.name) && (
                                                    <>
                                                        {showDropZone && (
                                                            <div className={styles.dropZoneRow}>
                                                                <div
                                                                    className={`${styles.sectionDropZoneOuter} ${styles.sectionDropZoneCompact} ${isDropTarget ? styles.sectionDropZoneOuterActive : ''}`}
                                                                    onDragOver={(e) => {
                                                                        e.preventDefault()
                                                                        setDragOverSection(section.name)
                                                                    }}
                                                                    onDragLeave={() =>
                                                                        setDragOverSection((prev) => prev === section.name ? null : prev)
                                                                    }
                                                                    onDrop={() => dropOntoEmptySection(section.name)}
                                                                >
                                                                    <div className={`${styles.sectionDropZoneInner} ${styles.sectionDropZoneInnerCompact} ${isDropTarget ? styles.sectionDropZoneInnerActive : ''}`}>
                                                                        <div className={styles.sectionDropZoneIcon}>
                                                                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                                                                                <polyline points="16 16 12 12 8 16" />
                                                                                <line x1="12" y1="12" x2="12" y2="21" />
                                                                                <path d="M20.39 18.39A5 5 0 0 0 18 9h-1.26A8 8 0 1 0 3 16.3" />
                                                                            </svg>
                                                                        </div>
                                                                        <p className={styles.sectionDropZoneText}>Drag &amp; Drop menu items here</p>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        )}
                                                        {section.items.length > 0 && renderMenuItems(section.items, null, 0, false, section.name)}
                                                    </>
                                                )}
                                            </div>
                                        )
                                    })}
                                </div>
                            </div>
                            <div className={styles.groupActions}>
                                <Button
                                    type="button"
                                    variant="ghost"
                                    className={styles.groupActionBtn}
                                    onClick={handleGroupSelected}
                                    disabled={selectedCheckedMeta.length < 2}
                                >
                                    <MaterialIcon name="group_work" size={14} /> Group Selected
                                </Button>
                                <Button
                                    type="button"
                                    variant="ghost"
                                    className={styles.groupActionBtn}
                                    onClick={handleUngroupSelected}
                                    disabled={selectedGroupsForUngroup.length === 0}
                                >
                                    <MaterialIcon name="ungroup" size={14} /> Ungroup Selected
                                </Button>
                            </div>
                            <Separator />
                            <Button type="button" variant="outline" className={styles.addSectionBtn} onClick={handleAddSection}>
                                <MaterialIcon name="add" size={14} /> Add Section
                            </Button>
                            <Separator />
                            <div className={styles.treeSectionHeader}>
                                <div className={styles.completeHeader}>
                                    <div className={styles.menuHeader}>
                                        <span className={styles.treeSectionHeaderTitle}>Footer</span>
                                        <Button
                                            type="button"
                                            className={styles.sectionAccordionBtn}
                                            onClick={() => toggleSectionCollapse('__footer_section__')}
                                            aria-expanded={!collapsedSections.has('__footer_section__')}
                                        >
                                            <MaterialIcon
                                                name={collapsedSections.has('__footer_section__') ? 'chevron_right' : 'expand_more'}
                                                size={16}
                                            />
                                        </Button>
                                    </div>
                                    {collectBranchIds(footerItems).length > 0 && (() => {
                                        const allExpanded = collectBranchIds(footerItems).every(id => !collapsedBranchIds.has(id))
                                        return (
                                            <button
                                                type="button"
                                                className={styles.sectionExpandBtn}
                                                onClick={() => allExpanded
                                                    ? handleCollapseSection(footerItems)
                                                    : handleExpandSection(footerItems)
                                                }
                                            >
                                                <MaterialIcon name={allExpanded ? 'unfold_less' : 'unfold_more'} size={13} />
                                                {allExpanded ? 'Collapse all' : 'Expand all'}
                                            </button>
                                        )
                                    })()}
                                </div>
                            </div>
                            {!collapsedSections.has('__footer_section__') && (
                                <>
                                    <div className={styles.dropZoneRow}>
                                        <div
                                            className={`${styles.sectionDropZoneOuter} ${styles.sectionDropZoneCompact} ${dragOverSection === FOOTER_DROP_TARGET ? styles.sectionDropZoneOuterActive : ''}`}
                                            onDragOver={(e) => {
                                                e.preventDefault()
                                                setDragOverSection(FOOTER_DROP_TARGET)
                                            }}
                                            onDragLeave={() =>
                                                setDragOverSection((prev) => (prev === FOOTER_DROP_TARGET ? null : prev))
                                            }
                                            onDrop={() => dropOntoEmptySection(FOOTER_DROP_TARGET)}
                                        >
                                            <div className={`${styles.sectionDropZoneInner} ${styles.sectionDropZoneInnerCompact} ${dragOverSection === FOOTER_DROP_TARGET ? styles.sectionDropZoneInnerActive : ''}`}>
                                                <div className={styles.sectionDropZoneIcon}>
                                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                                                        <polyline points="16 16 12 12 8 16" />
                                                        <line x1="12" y1="12" x2="12" y2="21" />
                                                        <path d="M20.39 18.39A5 5 0 0 0 18 9h-1.26A8 8 0 1 0 3 16.3" />
                                                    </svg>
                                                </div>
                                                <p className={styles.sectionDropZoneText}>Drag &amp; Drop module here to send it to footer</p>
                                            </div>
                                        </div>
                                    </div>
                                    <div className={styles.footerList}>
                                        {renderMenuItems(footerItems, null, 0, true)}
                                    </div>
                                </>
                            )}
                        </>
                    )}
                </div>

                <div style={{ display: 'none' }} className={`${styles.navColumn} ${propertiesCollapsed ? styles.navColumnCollapsed : ''}`}>
                    <div className={styles.columnHeaderRow}>
                        {!propertiesCollapsed && (
                            <div className={styles.columnHeaderRowTitle}>
                                <h3 className={styles.columnTitle}>Menu Item Properties</h3>
                                <p className={styles.columnSubtitle}>Configure selected item</p>
                            </div>
                        )}
                        <Button
                            variant="ghost"
                            size="icon"
                            className={styles.columnToggleBtn}
                            onClick={() => setPropertiesCollapsed((prev) => !prev)}
                            type="button"
                            aria-label={propertiesCollapsed ? 'Expand Menu Item Properties' : 'Collapse Menu Item Properties'}
                        >
                            <MaterialIcon name={propertiesCollapsed ? 'keyboard_double_arrow_left' : 'keyboard_double_arrow_right'} size={16} />
                        </Button>
                    </div>
                    {!propertiesCollapsed && (
                        <>
                            <Separator />
                            {selectedItem && (
                                <div className={styles.propertiesForm}>
                                    <div className={styles.propField}>
                                        <Label>Menu Label</Label>
                                        <Input value={selectedItem.label} placeholder="Menu label" onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleLabelChange(e.target.value)} />
                                    </div>
                                    <div className={styles.propField}>
                                        <Label>Select a module to connect</Label>
                                        <Select
                                            value={
                                                selectedItem.moduleId != null
                                                    ? String(selectedItem.moduleId)
                                                    : '__none__'
                                            }
                                            onValueChange={handleModuleChange}
                                            disabled={isModulesLoading}
                                        >
                                            <SelectTrigger>
                                                <SelectValue
                                                    placeholder={
                                                        isModulesLoading ? 'Loading modules...' : 'Select a module'
                                                    }
                                                />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="__none__">No module</SelectItem>
                                                {tenantModules.map((mod) => (
                                                    <SelectItem key={mod.module_id} value={String(mod.module_id)}>
                                                        {mod.module_name}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                        {modulesError && (
                                            <span className={styles.helperText}>
                                                Failed to load modules. Please try again later.
                                            </span>
                                        )}
                                    </div>
                                    <div className={styles.propField}>
                                        <Label>Select a feature to connect</Label>
                                        <Select
                                            value={
                                                selectedItem.featureId != null
                                                    ? String(selectedItem.featureId)
                                                    : '__none__'
                                            }
                                            onValueChange={handleFeatureChange}
                                            disabled={isFeaturesLoading || selectedItem.moduleId == null}
                                        >
                                            <SelectTrigger>
                                                <SelectValue
                                                    placeholder={
                                                        selectedItem.moduleId == null
                                                            ? 'Select a module first'
                                                            : isFeaturesLoading
                                                                ? 'Loading features...'
                                                                : 'Select a feature'
                                                    }
                                                />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="__none__">No feature</SelectItem>
                                                {moduleFeatures.map((feat) => (
                                                    <SelectItem key={feat.feature_id} value={String(feat.feature_id)}>
                                                        {feat.feature_name}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                        {featuresError && (
                                            <span className={styles.helperText}>
                                                Failed to load features. Please try again later.
                                            </span>
                                        )}
                                    </div>
                                    {!(selectedItem.hasSubmenu || selectedItem.children.length > 0) && (
                                        <div className={styles.propField}>
                                            <Label>Link path to a page</Label>
                                            <Select value={selectedItem.path || '__none__'} onValueChange={handlePathChange}>
                                                <SelectTrigger><SelectValue placeholder="Select a page" /></SelectTrigger>
                                                <SelectContent>
                                                    <SelectItem value="__none__">No link</SelectItem>
                                                    {routeOptions.map((route) => (
                                                        <SelectItem key={route} value={route}>{route}</SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                        </div>
                                    )}
                                    <div className={styles.propField}>
                                        <Label>Icon Name</Label>
                                        <Select value={selectedItem.icon} onValueChange={handleIconChange}>
                                            <SelectTrigger><SelectValue placeholder="LayoutDashboard" /></SelectTrigger>
                                            <SelectContent className={styles.iconSelectDropdown}>
                                                {AVAILABLE_ICONS.map((iconName) => (
                                                    <SelectItem key={iconName} value={iconName}>
                                                        <div className={styles.iconSelectItem}>
                                                            <MaterialIcon
                                                                name={resolveMaterialIconName(iconName)}
                                                                size={16}
                                                                className={styles.iconSelectItemIcon}
                                                            />
                                                            <span>{iconName}</span>
                                                        </div>
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <div className={styles.propSwitch}>
                                        <Checkbox id={`${selectedItem.id}_visible`} checked={selectedItem.visible} onCheckedChange={(checked) => handleVisibleChange(checked === true)} />
                                        <Label htmlFor={`${selectedItem.id}_visible`}>Visible</Label>
                                    </div>
                                    <Separator />
                                    <div className={styles.propSwitch}>
                                        <Checkbox
                                            id={`${selectedItem.id}_submenu`}
                                            checked={selectedItem.hasSubmenu || selectedItem.children.length > 0}
                                            onCheckedChange={(checked) => handleSubmenuChange(checked === true)}
                                        />
                                        <Label htmlFor={`${selectedItem.id}_submenu`}>Has more features</Label>
                                    </div>
                                    {(selectedItem.hasSubmenu || selectedItem.children.length > 0) && (
                                        <Button type="button" variant="outline" className={styles.addFeatureBtn} onClick={handleAddFeature}>
                                            <MaterialIcon name="add" size={14} /> Add Feature
                                        </Button>
                                    )}
                                    <Separator />
                                    <div className={styles.propField}>
                                        <Label>Menu Item Enabled For</Label>
                                        <div className={styles.checkboxGroup}>
                                            {ROLE_OPTIONS.map((role) => (
                                                <div key={role} className={styles.checkboxRow}>
                                                    <Checkbox
                                                        id={`${selectedItem.id}_${role}`}
                                                        checked={selectedItem.enabledFor.includes(role)}
                                                        onCheckedChange={(checked) => handleRoleToggle(role, checked === true)}
                                                    />
                                                    <label htmlFor={`${selectedItem.id}_${role}`}>{role[0]?.toUpperCase() + role.slice(1)}</label>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                    <Button
                                        variant="outline"
                                        className={styles.deleteBtnOutline}
                                        onClick={handleDelete}
                                    >
                                        <MaterialIcon
                                            name="delete"
                                            size={14}
                                            className={styles.deleteIcon}
                                        />{' '}
                                        Delete Menu Item
                                    </Button>
                                </div>
                            )}
                            {!selectedItem && (
                                <div className={styles.emptyProperties}>Select a menu item to edit properties.</div>
                            )}
                        </>
                    )}
                </div>
            </div>

            {/* ── Section name edit modal ── */}
            <Dialog.Root open={editingSectionName != null} onOpenChange={(open) => !open && cancelSectionEdit()}>
                <Dialog.Portal>
                    <Dialog.Overlay className={styles.sectionModalOverlay} />
                    <Dialog.Content className={styles.sectionModalContent}>
                        <div className={styles.sectionModalHeader}>
                            <Dialog.Title className={styles.sectionModalTitle}>Section Name</Dialog.Title>
                            <Dialog.Close asChild>
                                <Button type="button" variant="ghost" size="icon" className={styles.sectionModalClose}>
                                    <MaterialIcon name="close" size={16} />
                                </Button>
                            </Dialog.Close>
                        </div>
                        <p className={styles.sectionModalHint}>Enter a Name for the menu section</p>
                        <div className={styles.sectionModalBody}>
                            <Label>Enter Menu Section name</Label>
                            <Input
                                value={editingSectionDraft}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEditingSectionDraft(e.target.value)}
                                onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                                    if (e.key === 'Enter') saveSectionEdit()
                                    if (e.key === 'Escape') cancelSectionEdit()
                                }}
                                className={styles.sectionModalInput}
                                autoFocus
                            />
                        </div>
                        <div className={styles.sectionModalActions}>
                            <Button type="button" variant="outline" onClick={cancelSectionEdit}>
                                Cancel
                            </Button>
                            <Button type="button" onClick={saveSectionEdit}>
                                Save
                            </Button>
                        </div>
                    </Dialog.Content>
                </Dialog.Portal>
            </Dialog.Root>

            {/* ── Group edit modal ── */}
            <Dialog.Root open={editingGroupId != null} onOpenChange={(open) => !open && cancelGroupEdit()}>
                <Dialog.Portal>
                    <Dialog.Overlay className={styles.sectionModalOverlay} />
                    <Dialog.Content className={styles.groupModalContent}>
                        <div className={styles.sectionModalHeader}>
                            <Dialog.Title className={styles.groupModalTitle}>
                                <MaterialIcon name="group_work" size={16} />
                                Grouping
                            </Dialog.Title>
                            <Dialog.Close asChild>
                                <Button type="button" variant="ghost" size="icon" className={styles.sectionModalClose}>
                                    <MaterialIcon name="close" size={16} />
                                </Button>
                            </Dialog.Close>
                        </div>
                        <p className={styles.sectionModalHint}>You have selected features to group under a feature</p>
                        <div className={styles.sectionModalBody}>
                            <Label>Enter Group name</Label>
                            <Input
                                value={editingGroupDraft}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEditingGroupDraft(e.target.value)}
                                onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                                    if (e.key === 'Enter') saveGroupEdit()
                                    if (e.key === 'Escape') cancelGroupEdit()
                                }}
                                className={styles.sectionModalInput}
                                autoFocus
                            />
                            <div className={styles.groupSelectedWrap}>
                                <span className={styles.groupSelectedLabel}>Features Selected :</span>
                                <div className={styles.groupSelectedValues}>
                                    {editingGroupItem?.children.map((child) => child.label).join('  ')}
                                </div>
                            </div>
                        </div>
                        <div className={styles.groupModalActions}>
                            <Button type="button" variant="outline" className={styles.groupDeleteBtn} onClick={deleteGroupFromModal}>
                                <MaterialIcon name="delete" size={12} /> Delete Group
                            </Button>
                            <Button type="button" variant="outline" onClick={cancelGroupEdit}>
                                Cancel
                            </Button>
                            <Button type="button" onClick={saveGroupEdit}>
                                Save
                            </Button>
                        </div>
                    </Dialog.Content>
                </Dialog.Portal>
            </Dialog.Root>

            {/* ── Edit menu item modal ── */}
            <Dialog.Root open={editingItemId != null} onOpenChange={(open) => !open && cancelItemEdit()}>
                <Dialog.Portal>
                    <Dialog.Overlay className={styles.sectionModalOverlay} />
                    <Dialog.Content className={styles.itemEditModalContent}>
                        <div className={styles.sectionModalHeader}>
                            <Dialog.Title className={styles.itemEditModalTitle}>
                                Edit Label
                            </Dialog.Title>
                            <Dialog.Close asChild>
                                <Button type="button" variant="ghost" size="icon" className={styles.sectionModalClose}>
                                    <MaterialIcon name="close" size={16} />
                                </Button>
                            </Dialog.Close>
                        </div>
                        <p className={styles.sectionModalHint}>Edit menu item. The changes will reflect in the navigation tree and preview</p>
                        <div className={styles.itemEditModalBody}>
                            <div className={styles.propField}>
                                <Label>Enter Menu Item</Label>
                                <Input
                                    value={editingItemDraft.label ?? ''}
                                    placeholder="Enter menu item name"
                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                        setEditingItemDraft((prev) => ({ ...prev, label: e.target.value }))
                                    }
                                    onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                                        if (e.key === 'Enter') saveItemEdit()
                                        if (e.key === 'Escape') cancelItemEdit()
                                    }}
                                    className={styles.sectionModalInput}
                                    autoFocus
                                />
                            </div>
                            <div className={styles.propField}>
                                <Label>Default Module Name</Label>
                                <Input
                                    value={editingItem?.moduleName || editingItem?.label || ''}
                                    disabled
                                    className={styles.sectionModalInput}
                                />
                            </div>
                            {!(editingItem?.isGroup === true || editingItem?.id?.startsWith('group_') || editingItem?.featureId != null) && (
                                <div className={styles.propField}>
                                    <Label>Menu Item Icon</Label>
                                    <div className={styles.iconRowWrapper}>
                                        <div className={styles.iconDisplayGroup}>
                                            <MaterialIcon
                                                name={resolveMaterialIconName(editingItemDraft.icon ?? DEFAULT_ICON)}
                                                size={24}
                                                className={styles.iconDisplayIcon}
                                            />
                                            <Button
                                                type="button"
                                                variant="ghost"
                                                size="icon"
                                                className={styles.iconEditButton}
                                                onClick={() => setIconSelectionOpen(true)}
                                            >
                                                <MaterialIcon name="edit" size={14} />
                                            </Button>
                                        </div>
                                    </div>
                                </div>
                            )}
                            <div className={styles.propField}>
                              
                                <div className={styles.typeWrapper}>
                                    <span className={styles.typeLabel}>Type:</span>
                                    <div className={styles.typeBadgeContainer}>
                                        <span className={`${styles.typeBadge} ${getTypeBadgeClassName(getItemTypeLabel(editingItem))}`.trim()}>
                                            {getItemTypeLabel(editingItem)}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className={styles.itemEditModalActions}>
                            <Button type="button" variant="outline" onClick={cancelItemEdit}>
                                Cancel
                            </Button>
                            <Button type="button" onClick={saveItemEdit}>
                                Save
                            </Button>
                        </div>
                    </Dialog.Content>
                </Dialog.Portal>
            </Dialog.Root>

            {/* ── Icon Selection Modal ── */}
            <Dialog.Root open={iconSelectionOpen} onOpenChange={(open) => {
                setIconSelectionOpen(open)
                if (!open) setIconSearchTerm('')
            }}>
                <Dialog.Portal>
                    <Dialog.Overlay className={styles.sectionModalOverlay} />
                    <Dialog.Content className={styles.iconSelectionModal}>
                        <div className={styles.iconSelectionHeader}>
                            <div>
                                <Dialog.Title className={styles.iconSelectionTitle}>
                                    Select Icon
                                </Dialog.Title>
                                <p className={styles.iconSelectionHint}>Select Icon from the list of icons</p>
                            </div>
                            <div className={styles.iconSelectionHeaderRight}>
                                <div className={styles.iconSearchWrapper}>
                                    <div className={styles.iconSearchContainer}>
                                        <MaterialIcon name="search" size={16} className={styles.searchIcon} />
                                        <input
                                            type="text"
                                            placeholder="Search icons..."
                                            value={iconSearchTerm}
                                            onChange={(e) => setIconSearchTerm(e.target.value)}
                                            className={styles.iconSearchInput}
                                            autoFocus
                                        />
                                        {iconSearchTerm && (
                                            <button
                                                className={styles.clearSearchBtn}
                                                onClick={() => setIconSearchTerm('')}
                                            >
                                                <MaterialIcon name="close" size={14} />
                                            </button>
                                        )}
                                    </div>
                                </div>
                                <Dialog.Close asChild>
                                    <Button
                                        type="button"
                                        variant="ghost"
                                        size="icon"
                                        className={styles.iconSelectionCloseBtn}
                                        aria-label="Close"
                                    >
                                        <MaterialIcon name="close" size={18} />
                                    </Button>
                                </Dialog.Close>
                            </div>
                        </div>

                        <div className={styles.iconSelectionBody}>
                            {(() => {
                                const getGroupedIconsWithSearch = () => {
                                    if (!iconSearchTerm || iconSearchTerm.trim() === '') {
                                        return ICON_CATEGORIES
                                    }
                                    const term = iconSearchTerm.toLowerCase().trim()
                                    const grouped: Record<string, string[]> = {}
                                    Object.entries(ICON_CATEGORIES).forEach(([category, icons]) => {
                                        const filteredIcons = icons.filter(icon =>
                                            icon.toLowerCase().includes(term)
                                        )
                                        if (filteredIcons.length > 0) {
                                            grouped[category] = filteredIcons
                                        }
                                    })
                                    return grouped
                                }

                                const groupedIcons = getGroupedIconsWithSearch()
                                const hasResults = Object.keys(groupedIcons).length > 0

                                if (!hasResults && iconSearchTerm) {
                                    return (
                                        <div className={styles.noResults}>
                                            <MaterialIcon name="search_off" size={48} className={styles.noResultsIcon} />
                                            <p className={styles.noResultsText}>No icons found for "{iconSearchTerm}"</p>
                                        </div>
                                    )
                                }

                                return (
                                    <>
                                        {Object.entries(groupedIcons).map(([category, icons]) => (
                                            <div key={category} className={styles.iconCategorySection}>
                                                <div className={styles.iconCategoryTitle}>
                                                    <MaterialIcon
                                                        name={getCategoryIcon(category)}
                                                        size={16}
                                                        className={styles.categoryIcon}
                                                    />
                                                    <span>{category}</span>
                                                </div>
                                                <div className={styles.iconGrid}>
                                                    {icons.map((iconName) => (
                                                        <div
                                                            key={iconName}
                                                            className={`${styles.iconOption} ${editingItemDraft.icon === iconName ? styles.iconOptionSelected : ''}`}
                                                            onClick={() => {
                                                                setEditingItemDraft((prev) => ({ ...prev, icon: iconName }))
                                                                setIconSelectionOpen(false)
                                                                setIconSearchTerm('')
                                                            }}
                                                        >
                                                            <div className={styles.iconPreview}>
                                                                <MaterialIcon
                                                                    name={resolveMaterialIconName(iconName)}
                                                                    size={28}
                                                                    className={styles.iconOptionIcon}
                                                                />
                                                            </div>
                                                            <span className={styles.iconOptionName}>{iconName}</span>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        ))}
                                    </>
                                )
                            })()}
                        </div>
                        <div className={styles.iconSelectionActions}>
                            <Button type="button" variant="outline" onClick={() => {
                                setIconSelectionOpen(false)
                                setIconSearchTerm('')
                            }}>
                                Cancel
                            </Button>
                            <Button type="button" onClick={() => {
                                setIconSelectionOpen(false)
                                setIconSearchTerm('')
                            }}>
                                Select
                            </Button>
                        </div>
                    </Dialog.Content>
                </Dialog.Portal>
            </Dialog.Root>
        </div>
    )
}